/**
 * materialAreaMap.ts — S0 (03/07/2026) · v2 FINAL  [TWIN FRONTEND]
 * ─────────────────────────────────────────────────────────────────
 * Cópia frontend da fonte única de roteamento material → área.
 * ⚠ Mantém em sync com worker/src/lib/materialAreaMap.ts (cópia manual,
 *   igual a types.ts — não há pacote compartilhado).
 *
 * Base: RELATORIO_MATERIAL_x_AREA_20260703 (matriz v2, revisada por Renan).
 * Regras: 7 áreas (Design→Branding; Dados→Análise); dono vs apoio;
 * externo (fornecedor); donoSolicitante (podcast/treinamento); canalAplica.
 */

// ─── Áreas (IDs Airtable · base appnIr391gfBnEcAD) — twin de eventTemplates.ts ─
export const AREA = {
  EVENTOS: 'recMwBjMhWlVmT2mi',
  CONTEUDO: 'rectXabN73QOYRC87',
  BRANDING: 'recrLlqYd5JlYT2Ik',
  PRODUTORA: 'recDoKtxEGIswPNdZ',
  COMERCIAL: 'recX1KQ8S6XmlXoMk',
  MAC: 'rec8xJXYPFPYDgJRb',
  ANALISE: 'recYTgvTIEYx5kc0h',
} as const;

/** ID legado da antiga área "Dados" — consolidada em Análise (v2). @deprecated */
export const AREA_DADOS_LEGADO = 'reccewSGox8kJnlSL';

export function normalizeAreaId(id: string): string {
  return id === AREA_DADOS_LEGADO ? AREA.ANALISE : id;
}
export function normalizeAreaIds(ids: string[]): string[] {
  return [...new Set((ids ?? []).map(normalizeAreaId))];
}

// ─── Grupos e materiais (taxonomia v2) ──────────────────────────────────────
export type GrupoMaterial =
  | 'Audiovisual-TV'
  | 'Audiovisual-Digital'
  | 'Social'
  | 'Editorial'
  | 'Branding'
  | 'Foto'
  | 'Pesquisa/Dados'
  | 'Evento'
  | 'MAC'
  | 'Comercial'
  | 'Institucional';

export type TipoMaterial =
  | 'Matéria de TV'
  | 'Chamada / escalada / teaser'
  | 'Fatos Relevantes'
  | 'Reel / vídeo curto / influenciador'
  | 'Vídeo institucional'
  | 'Aftermovie'
  | 'Transmissão ao vivo / palestra'
  | 'Spot / Boletim de rádio'
  | 'Sonora'
  | 'Podcast'
  | 'Post / carrossel / stories'
  | 'Cobertura em tempo real'
  | 'Newsletter / e-mail'
  | 'Reportagem / artigo / editorial'
  | 'Release / assessoria / plano de comunicação'
  | 'Relatório de visibilidade / CSAT'
  | 'Manual de marca / rebranding / logo / ID'
  | 'KV / conceito de campanha'
  | 'Apresentação / deck'
  | 'Onepager'
  | 'Convite / save-the-date'
  | 'Backdrop / impressos'
  | 'Site / landing page'
  | 'Template'
  | 'Telas animadas / motion'
  | 'Campanha'
  | 'Cobertura de evento por polo'
  | 'Fotografia / galeria'
  | 'Pesquisa / estudo / relatório'
  | 'Visualização de dados / gráficos / painel'
  | 'Book / manuais / grade / cerimonial'
  | 'Ativação / enxoval / landing / report MAC'
  | 'Proposta comercial'
  | 'Treinamento interno';

export interface AreaRoteamento {
  primaria: string;
  apoio: string[];
  grupo: GrupoMaterial;
  externo?: boolean;
  donoSolicitante?: boolean;
  canalAplica?: boolean;
  estruturaNaoCard?: boolean;
}

export const MATERIAL_AREA_MAP: Record<TipoMaterial, AreaRoteamento> = {
  'Matéria de TV': { primaria: AREA.CONTEUDO, apoio: [AREA.PRODUTORA], grupo: 'Audiovisual-TV', canalAplica: true },
  'Chamada / escalada / teaser': { primaria: AREA.CONTEUDO, apoio: [AREA.PRODUTORA], grupo: 'Audiovisual-TV', canalAplica: true },
  'Fatos Relevantes': { primaria: AREA.CONTEUDO, apoio: [AREA.PRODUTORA], grupo: 'Audiovisual-TV', canalAplica: true },
  'Reel / vídeo curto / influenciador': { primaria: AREA.CONTEUDO, apoio: [AREA.PRODUTORA], grupo: 'Audiovisual-Digital', canalAplica: true },
  'Vídeo institucional': { primaria: AREA.PRODUTORA, apoio: [AREA.BRANDING, AREA.CONTEUDO], grupo: 'Audiovisual-Digital' },
  'Aftermovie': { primaria: AREA.PRODUTORA, apoio: [AREA.CONTEUDO], grupo: 'Audiovisual-Digital' },
  'Transmissão ao vivo / palestra': { primaria: AREA.PRODUTORA, apoio: [], grupo: 'Audiovisual-Digital' },
  'Spot / Boletim de rádio': { primaria: AREA.CONTEUDO, apoio: [AREA.COMERCIAL], grupo: 'Audiovisual-Digital', externo: true },
  'Sonora': { primaria: AREA.PRODUTORA, apoio: [AREA.CONTEUDO], grupo: 'Audiovisual-Digital' },
  'Podcast': { primaria: '', apoio: [AREA.PRODUTORA, AREA.BRANDING], grupo: 'Audiovisual-Digital', donoSolicitante: true },
  'Post / carrossel / stories': { primaria: AREA.CONTEUDO, apoio: [AREA.BRANDING], grupo: 'Social', canalAplica: true },
  'Cobertura em tempo real': { primaria: AREA.CONTEUDO, apoio: [AREA.PRODUTORA], grupo: 'Social' },
  'Newsletter / e-mail': { primaria: AREA.CONTEUDO, apoio: [AREA.BRANDING], grupo: 'Social' },
  'Reportagem / artigo / editorial': { primaria: AREA.CONTEUDO, apoio: [], grupo: 'Editorial', canalAplica: true },
  'Release / assessoria / plano de comunicação': { primaria: AREA.CONTEUDO, apoio: [], grupo: 'Editorial' },
  'Relatório de visibilidade / CSAT': { primaria: AREA.CONTEUDO, apoio: [AREA.EVENTOS], grupo: 'Editorial' },
  'Manual de marca / rebranding / logo / ID': { primaria: AREA.BRANDING, apoio: [], grupo: 'Branding' },
  'KV / conceito de campanha': { primaria: AREA.BRANDING, apoio: [AREA.CONTEUDO], grupo: 'Branding' },
  'Apresentação / deck': { primaria: AREA.BRANDING, apoio: [AREA.CONTEUDO], grupo: 'Branding' },
  'Onepager': { primaria: AREA.BRANDING, apoio: [], grupo: 'Branding' },
  'Convite / save-the-date': { primaria: AREA.BRANDING, apoio: [], grupo: 'Branding' },
  'Backdrop / impressos': { primaria: AREA.BRANDING, apoio: [], grupo: 'Branding' },
  'Site / landing page': { primaria: AREA.BRANDING, apoio: [AREA.MAC], grupo: 'Branding' },
  'Template': { primaria: AREA.BRANDING, apoio: [], grupo: 'Branding' },
  'Telas animadas / motion': { primaria: AREA.BRANDING, apoio: [AREA.PRODUTORA], grupo: 'Branding' },
  'Campanha': { primaria: AREA.CONTEUDO, apoio: [AREA.BRANDING, AREA.PRODUTORA], grupo: 'Social', canalAplica: true },
  'Cobertura de evento por polo': { primaria: AREA.PRODUTORA, apoio: [], grupo: 'Foto', externo: true },
  'Fotografia / galeria': { primaria: AREA.PRODUTORA, apoio: [], grupo: 'Foto', externo: true },
  'Pesquisa / estudo / relatório': { primaria: AREA.ANALISE, apoio: [AREA.BRANDING, AREA.CONTEUDO], grupo: 'Pesquisa/Dados' },
  'Visualização de dados / gráficos / painel': { primaria: AREA.ANALISE, apoio: [], grupo: 'Pesquisa/Dados' },
  'Book / manuais / grade / cerimonial': { primaria: AREA.EVENTOS, apoio: [AREA.BRANDING], grupo: 'Evento' },
  'Ativação / enxoval / landing / report MAC': { primaria: AREA.MAC, apoio: [AREA.BRANDING, AREA.CONTEUDO], grupo: 'MAC' },
  'Proposta comercial': { primaria: AREA.COMERCIAL, apoio: [AREA.BRANDING], grupo: 'Comercial' },
  'Treinamento interno': { primaria: '', apoio: [AREA.PRODUTORA, AREA.BRANDING], grupo: 'Institucional', donoSolicitante: true },
};

export const TODOS_MATERIAIS = Object.keys(MATERIAL_AREA_MAP) as TipoMaterial[];

export const MATERIAIS_POR_GRUPO: Record<GrupoMaterial, TipoMaterial[]> = TODOS_MATERIAIS.reduce(
  (acc, m) => {
    const g = MATERIAL_AREA_MAP[m].grupo;
    (acc[g] ??= []).push(m);
    return acc;
  },
  {} as Record<GrupoMaterial, TipoMaterial[]>,
);

export function isTipoMaterial(v: unknown): v is TipoMaterial {
  return typeof v === 'string' && v in MATERIAL_AREA_MAP;
}

/** Coordenadores acionados ao informar o Canal (v2). */
export const COORDENADORES_CANAL: Record<'digital' | 'tv', string[]> = {
  digital: ['Valentina (Gerente de Conteúdos)', 'Agnes (coord. Conteúdo Digital)'],
  tv: ['Fernanda Batista (Editora-executiva MB / Coord. Produtora)'],
};

// ─── Bridge: TipoProduto (engine de checklist, legado) → TipoMaterial (v2) ───
export const TIPO_PRODUTO_TO_MATERIAL: Record<string, TipoMaterial> = {
  'Matéria de TV': 'Matéria de TV',
  'Cliente Digital': 'Reel / vídeo curto / influenciador',
  'Evento inter time': 'Book / manuais / grade / cerimonial',
  'Conteúdo editorial': 'Reportagem / artigo / editorial',
  'Vídeo institucional': 'Vídeo institucional',
  'Foto / Galeria': 'Fotografia / galeria',
  'Post de rede social': 'Post / carrossel / stories',
  'Pesquisa': 'Pesquisa / estudo / relatório',
  'Campanha': 'Campanha',
  // Novos tipos (08/jul/2026): cobertura roteia como post/stories (dono Conteúdo,
  // Produtora entra como apoio de captação); vídeo p/ evento roteia como vídeo institucional.
  'Cobertura de evento': 'Post / carrossel / stories',
  'Vídeo para evento': 'Vídeo institucional',
};

const KEYWORD_MATERIAL: { re: RegExp; material: TipoMaterial }[] = [
  { re: /aftermovie/i, material: 'Aftermovie' },
  { re: /galeria|fotos?|fotografia/i, material: 'Fotografia / galeria' },
  { re: /v[íi]deo\s+institucional|institucional/i, material: 'Vídeo institucional' },
  // Rádio (externo) — antes de teaser/matéria (retorno Comercial 09/07)
  { re: /boletim|\bspot\b|jovem\s*pan|fm\s*o\s*dia|r[áa]dio/i, material: 'Spot / Boletim de rádio' },
  // "Marcação" é post (Conteúdo), NÃO logo — precede a regra de logo/marca
  { re: /marca[çc][ãa]o/i, material: 'Post / carrossel / stories' },
  { re: /teaser|chamada|escalada/i, material: 'Chamada / escalada / teaser' },
  { re: /transmiss[ãa]o|ao\s*vivo|palestra/i, material: 'Transmissão ao vivo / palestra' },
  { re: /reel|influencia|v[íi]deo\s*curto/i, material: 'Reel / vídeo curto / influenciador' },
  { re: /v[íi]deo/i, material: 'Vídeo institucional' },
  { re: /save\s*the\s*date|convite/i, material: 'Convite / save-the-date' },
  { re: /backdrop|impress|gr[áa]fic|banner/i, material: 'Backdrop / impressos' },
  { re: /onepager|one\s*pager/i, material: 'Onepager' },
  { re: /landing|site|hotsite/i, material: 'Site / landing page' },
  { re: /apresenta[çc][ãa]o|deck|slides?/i, material: 'Apresentação / deck' },
  { re: /logo|logomarca|identidade|marca|\bID\b|\bKV\b/i, material: 'Manual de marca / rebranding / logo / ID' },
  { re: /release|assessoria|imprensa|plano\s+de\s+comunica/i, material: 'Release / assessoria / plano de comunicação' },
  { re: /publieditorial/i, material: 'Reportagem / artigo / editorial' },
  // Disparos por canais (mailing/WhatsApp) entram como newsletter/e-mail
  { re: /newsletter|disparo|mailing|whats\s*app|e-?mail\s*mkt|e-?mail\s*marketing|e-?mail/i, material: 'Newsletter / e-mail' },
  { re: /relat[óo]rio.*(visibilidade|csat)|csat|visibilidade/i, material: 'Relatório de visibilidade / CSAT' },
  { re: /pesquisa|estudo|relat[óo]rio/i, material: 'Pesquisa / estudo / relatório' },
  { re: /gr[áa]fico|dashboard|painel|visualiza/i, material: 'Visualização de dados / gráficos / painel' },
  { re: /post|carrossel|stor(y|ies)|rede\s*social/i, material: 'Post / carrossel / stories' },
  // VT/vinheta/inserção na grade = programa MB (Produtora via canal=tv) — antes de book(grade)/matéria
  { re: /\bvt\b|vinheta|inser[çc][ãa]o.*grade/i, material: 'Matéria de TV' },
  { re: /book|cerimonial|grade|manual/i, material: 'Book / manuais / grade / cerimonial' },
  { re: /ativa[çc]|a[çc][ãa]o\s+promocional|enxoval|\bMAC\b/i, material: 'Ativação / enxoval / landing / report MAC' },
  { re: /mat[ée]ria/i, material: 'Matéria de TV' },
];

export function materialDoTexto(texto: string): TipoMaterial | null {
  const t = texto ?? '';
  for (const { re, material } of KEYWORD_MATERIAL) if (re.test(t)) return material;
  return null;
}

export function resolveAreasPorTexto(
  texto: string,
  solicitanteAreaId?: string,
  canal?: 'digital' | 'tv',
): { areasIds: string[]; areasApoioIds: string[]; material: TipoMaterial | null; externo: boolean } {
  const material = materialDoTexto(texto);
  if (!material) return { areasIds: [], areasApoioIds: [], material: null, externo: false };
  const r = resolveAreasMaterial(material, solicitanteAreaId, canal);
  return { ...r, material };
}

export function resolveAreasTipoProduto(
  tipoProduto: string,
  solicitanteAreaId?: string,
  canal?: 'digital' | 'tv',
): { areasIds: string[]; areasApoioIds: string[]; material: TipoMaterial | null; externo: boolean } {
  const material = TIPO_PRODUTO_TO_MATERIAL[tipoProduto];
  if (!material) return { areasIds: [], areasApoioIds: [], material: null, externo: false };
  const r = resolveAreasMaterial(material, solicitanteAreaId, canal);
  return { ...r, material };
}

/**
 * Resolve as áreas (dono + apoio) de um material.
 * Regra por canal (v2.1, revisada por Renan 06/07): materiais que exigem canal
 * (canalAplica) roteiam por canal — canal=tv → Produtora dona (Fernanda Batista),
 * com o dono anterior (ex.: Conteúdo) rebaixado a apoio; canal=digital mantém o
 * mapa (Conteúdo dono, Valentina/Agnes). Sem canal informado, usa o mapa base.
 */
export function resolveAreasMaterial(
  material: TipoMaterial,
  solicitanteAreaId?: string,
  canal?: 'digital' | 'tv',
): { areasIds: string[]; areasApoioIds: string[]; externo: boolean } {
  const r = MATERIAL_AREA_MAP[material];
  if (!r) return { areasIds: [], areasApoioIds: [], externo: false };
  let dono = r.donoSolicitante
    ? (solicitanteAreaId ? [solicitanteAreaId] : [])
    : (r.primaria ? [r.primaria] : []);
  let apoio = r.apoio;
  if (r.canalAplica && canal === 'tv') {
    // TV → Produtora dona; dono anterior (Conteúdo) vira apoio, demais apoios mantidos.
    const anteriores = [...dono, ...apoio].filter((a) => a && a !== AREA.PRODUTORA);
    dono = [AREA.PRODUTORA];
    apoio = anteriores;
  }
  return {
    areasIds: dono,
    areasApoioIds: apoio.filter((a) => !dono.includes(a)),
    externo: !!r.externo,
  };
}
